import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useSWR, { Fetcher } from 'swr';

import { axiosInstanceForTenantAPI } from '../../../../../assets/utils/axiosInstance';
import getStatusOptions from '../../../../../constants/statusOptions';
import { DynamicFieldResponse } from '../../../../../interfaces/DynamicFieldResponse';
import ErrorType from '../../../../../interfaces/ErrorType';
import { FinalReportData } from '../../../../../interfaces/FinalReportData';
import { FinalReportDocumentField } from '../../../../../interfaces/FinalReportDocumentField';
import {
  InformedIndividualItemType,
  ModifiedFinalReportDocument,
} from '../../../../../interfaces/ModifiedFinalReportDocument';
import {
  FinalReportItemInterface,
  setItem,
} from '../../../../../slices/finalReportSlice';
import { RootState } from '../../../../../utils/store';
import DownloadFinalReportButton from '../../../../common/finalReport/downloadReportButton';
import SnackBarErrorResponse from '../../../../common/snackBarErrorResponse';
import ViewReportFields from '../common/ViewReportFields';
import ViewReportFixedFields from '../common/ViewReportFixedFields';

interface ViewFinalReportDialogProps {
  open: boolean;
  onClose: () => void;
  id: number;
  disabledFlag?: boolean;
}

//intersection for final report data and item interface
//serves as an alias for combined interfaces
interface CombinedFinalReportData
  extends FinalReportData,
    FinalReportItemInterface {}

const ViewFinalReportDialog = ({
  open,
  onClose,
  id,
  disabledFlag,
}: ViewFinalReportDialogProps) => {
  const dispatch = useDispatch();

  const departments = useSelector(
    (state: RootState) => state.reports.departments,
  );

  const informedIndividualItems = useSelector(
    (state: RootState) => state.informedIndividuals.items,
  );

  const FINAL_REPORT_URL = `/final-reports/${id}`;
  const fetcher: Fetcher<CombinedFinalReportData, string> = async (url) => {
    const response = await axiosInstanceForTenantAPI().get(url);
    return response.data;
  };
  const { data, error, isLoading } = useSWR(FINAL_REPORT_URL, fetcher);

  //set final report reducer in order to tie information with informedindividualsection component
  dispatch(
    setItem({
      status: data?.status ?? 0,
      has_informed_individuals: data?.has_informed_individuals ?? false,
      informed_individuals: data?.informed_individuals ?? [],
    }),
  );

  const [snackbarError, setSnackbarError] = useState<ErrorType>({
    errorType: false,
    message: '',
  });

  const handleClose = () => {
    onClose();
  };

  const handleDisabledClick = () => {
    if (!disabledFlag) {
      setSnackbarError({
        errorType: true,
        message: 'You are not permitted to download this final report.',
        severity: 'info',
      });
    }
    return;
  };

  const handleCloseSnackbar = (reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarError((prevError) => ({ ...prevError, errorType: false }));
  };

  if (isLoading) {
    return (
      <Box
        width='100%'
        height='100%'
        display='flex'
        justifyContent='center'
        alignItems='center'
        flexGrow={1}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Typography>
          <FormattedMessage defaultMessage='An error occurred. Please try again later.' />
        </Typography>
      </Box>
    );
  }

  const getPriorityNumber = (priority: string) => {
    switch (priority) {
      case 'Low':
        return 0;
      case 'Medium':
        return 1;
      case 'High':
        return 2;
      case 'Critical':
        return 3;
      default:
        return -1;
    }
  };

  const statusOptions = getStatusOptions();

  const defaultFieldValues = {
    reportType: data.report_type,
    referenceID: data.report_reference,
    assignee: data.case_manager_name,
    priority: getPriorityNumber(data.report_severity),
    dateReceived: data.date_received
      ? dayjs(data.date_received).format('YYYY-MM-DD')
      : null,
  };

  const customFieldValues: DynamicFieldResponse[] =
    data.final_report_custom_field_values.map((field) => {
      const siblingFields: DynamicFieldResponse[] = field.siblings.map(
        (sibling) => {
          return {
            id: sibling.id,
            placeholder: sibling.final_report_custom_field_sibling.placeholder,
            label: sibling.final_report_custom_field_sibling.label,
            dropdown_choices:
              sibling.final_report_custom_field_sibling.dropdown_choices,
            is_mandatory:
              sibling.final_report_custom_field_sibling.is_mandatory,
            type: sibling.final_report_custom_field_sibling.type,
            value: sibling.value,
          };
        },
      );

      return {
        id: field.id,
        placeholder: field.final_report_custom_field.placeholder,
        label: field.final_report_custom_field.label,
        dropdown_choices: field.final_report_custom_field.dropdown_choices,
        is_mandatory: field.final_report_custom_field.is_mandatory,
        type: field.final_report_custom_field.type,
        value: field.value,
        siblings: siblingFields,
      };
    });

  const pdfFields: FinalReportDocumentField[] =
    data.final_report_custom_field_values.map((field) => {
      const siblingFields: FinalReportDocumentField[] = field.siblings.map(
        (sibling) => {
          return {
            title: sibling.final_report_custom_field_sibling.label,
            type: sibling.final_report_custom_field_sibling.type,
            value: sibling.value || '',
          };
        },
      );

      return {
        title: field.final_report_custom_field.label,
        type: field.final_report_custom_field.type,
        value: field.value || '',
        siblings: siblingFields,
      };
    });

  const getDepartmentName = (did: number): string => {
    let departmentName = '';
    const findDepartment = _.find(departments, (e) => e.value == did);
    if (typeof findDepartment != 'undefined')
      departmentName = findDepartment.label;

    return departmentName;
  };

  const modifiedItems: Array<InformedIndividualItemType> = [];

  if (informedIndividualItems.length > 0) {
    informedIndividualItems.map((item) => {
      modifiedItems.push({
        id: item.id,
        report: item.report,
        name: item.name,
        associatedDepartment: getDepartmentName(item.associated_department),
        notificationReason: item.notification_reason,
        dateInformed: dayjs(item.date_informed_at).format('LL'),
      });
    });
  }

  const pdfData: ModifiedFinalReportDocument = {
    title: data.title,
    status: statusOptions[data.report_status_integer]?.name,
    reportType: data.report_type,
    referenceID: data.report_number,
    assignee: data.case_manager_name,
    priority: data.report_severity,
    dateReceived: data.date_received
      ? dayjs(data.date_received).format('DD MMM YYYY')
      : null,
    fieldValues: pdfFields,
    dateCompleted: dayjs(data.completed_date).format('DD MMM YYYY'),
    informed_individuals: modifiedItems,
    has_informed_individuals: data.has_informed_individuals,
    caseManagerReferenceId: data.case_manager_reference_id,
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '1152px',
          height: '100%',
        },
      }}
      disableRestoreFocus
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          padding: '4px 8px 4px 20px',
          backgroundColor: '#F6F6F6',
        }}
      >
        <SnackBarErrorResponse
          open={snackbarError.errorType}
          message={snackbarError.message}
          handleCloseSnackbar={handleCloseSnackbar}
          severity={snackbarError.severity ? snackbarError.severity : 'error'}
        />
        <Typography>{data.title}</Typography>
        <Stack direction='row' spacing={2} alignItems='center'>
          <Button
            variant='text'
            color='darkGray'
            sx={{
              textTransform: 'none',
            }}
            onClick={handleClose}
            startIcon={<CloseIcon />}
          >
            <FormattedMessage defaultMessage='Close' />
          </Button>
        </Stack>
      </Stack>
      <Box
        flexGrow={1}
        padding='24px'
        overflow='auto'
        onClick={handleDisabledClick}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          paddingX='56px'
        >
          <Typography fontSize={24}>{data.title}</Typography>
          <DownloadFinalReportButton pdfData={pdfData} />
        </Stack>
        <ViewReportFixedFields defaultValues={defaultFieldValues} />
        <ViewReportFields customValues={customFieldValues} />
      </Box>
    </Dialog>
  );
};

export default ViewFinalReportDialog;
